import './TableFilterBar.scss';

import React, { useCallback, useMemo, useState } from 'react';

import { FilterOperator } from '../../../apis/graphql';
import Button from '../../../common/Button';
import { openToastAlert } from '../../../common/ToastAlert/toastAlert';
import { copyToClipboard } from '../../../common/utils';
import { TRAFFICKING_SEARCH_PARAMS } from '../../../constants';
import type { GlobalFilters } from '../../../contexts/FilterContext';
import { useFiltersContext } from '../../../contexts/FilterContext';
import bem from '../../../utils/bem';
import { genericMemo } from '../../../utils/genericMemo';
import FilterPanel from '../FilterPanel';
import { CustomSavedFiltersDropdown, ToolbarDriftWarning, useIsFilterDrifted } from '../FilterPanel/CustomSavedFilters';
import type { AdditionalFilterInDropdown } from './AdditionalFilters/constants';
import { FilterButton } from './FilterButton';
import {
  useAdditionalFilterDefinitions,
  useHandleDropdownFilters,
  useSaveTraffickingFilters,
  useSelectedFiltersValues,
  useSyncSelectedDropdownFilters,
} from './hooks';

export type TableFilterProps = {
  applyCommonFilter: <T extends keyof GlobalFilters>(filterName: T, filterValue: GlobalFilters[T]) => void;
  isFilterPanelOpen: boolean;
  onOpenFilterPanel: () => void;
  onCloseFilterPanel: () => void;
  scrollToFilterId?: string | null;
  clearScrollToFilterId?: () => void;
};

const [block, element] = bem('table-filter-bar');

const TableFilterBar = ({
  applyCommonFilter,
  isFilterPanelOpen,
  onOpenFilterPanel,
  onCloseFilterPanel,
  scrollToFilterId,
  clearScrollToFilterId,
}: TableFilterProps): React.JSX.Element => {
  const isDrifted = useIsFilterDrifted();
  const { saveFilters, loading } = useSaveTraffickingFilters();
  const {
    applyCustomFilter,
    applyFilter,
    filters: { traffickerEmailList, accountExecutiveList, displayOrder },
  } = useFiltersContext();

  const [selectedDropdownFilters, setSelectedDropdownFilters] = useState<AdditionalFilterInDropdown[]>([]);

  const selectedFiltersValues = useSelectedFiltersValues();

  // Redirects V1 Product Type writes to the V2 key (adProductList) and clears
  // the V1 key. Also redirects brand chip removals into brandIdOperator.
  const applyProductTypeFilter = useCallback(
    <T extends keyof GlobalFilters>(filterName: T, filterValue: GlobalFilters[T]) => {
      if (filterName === TRAFFICKING_SEARCH_PARAMS.PRODUCT_TYPE) {
        applyCommonFilter('adProductList', filterValue as GlobalFilters['adProductList']);
        applyCommonFilter(
          TRAFFICKING_SEARCH_PARAMS.PRODUCT_TYPE,
          null as GlobalFilters[typeof TRAFFICKING_SEARCH_PARAMS.PRODUCT_TYPE]
        );
      } else if (
        filterName === TRAFFICKING_SEARCH_PARAMS.BRAND_ID_LIST ||
        filterName === TRAFFICKING_SEARCH_PARAMS.EXCLUDE_BRAND_ID_LIST
      ) {
        const values = filterValue as GlobalFilters['brandIdList'];
        const operator =
          filterName === TRAFFICKING_SEARCH_PARAMS.EXCLUDE_BRAND_ID_LIST
            ? FilterOperator.DoesNotContain
            : FilterOperator.Contains;
        applyCommonFilter('brandIdOperator', values && values.length > 0 ? { operator, values } : null);
      } else {
        applyCommonFilter(filterName, filterValue);
      }
    },
    [applyCommonFilter]
  );

  const filterDefinitions = useAdditionalFilterDefinitions(selectedFiltersValues);
  useSyncSelectedDropdownFilters({
    filters: filterDefinitions,
    selectedFiltersValues,
    onSelectedDropdownFilters: setSelectedDropdownFilters,
  });

  const { isFilterChipShown, onRemoveAllFilter } = useHandleDropdownFilters({
    selectedFiltersValues,
    applyCommonFilter: applyProductTypeFilter,
    selectedDropdownFilters,
    onSelectedDropdownFilters: setSelectedDropdownFilters,
  });

  const totalFilterCount = useMemo(() => {
    let count = selectedDropdownFilters?.length ?? 0;
    const accountManagerValues = traffickerEmailList ?? [];
    const accountExecValues = accountExecutiveList ?? [];
    if (accountManagerValues.length > 0) count += 1;
    if (accountExecValues.length > 0) count += 1;
    return count;
  }, [selectedDropdownFilters, traffickerEmailList, accountExecutiveList]);

  const handleClearAll = useCallback(() => {
    onRemoveAllFilter();
    applyCustomFilter(null);
    if (displayOrder && displayOrder.length > 0) {
      applyFilter('displayOrder', [] as GlobalFilters[keyof GlobalFilters]);
    }
  }, [onRemoveAllFilter, applyCustomFilter, applyFilter, displayOrder]);

  const handleGenerateShareableUrl = useCallback(async (): Promise<void> => {
    try {
      const shareableUrl = await saveFilters();

      await copyToClipboard({
        text: shareableUrl,
        toastMessage: 'Shareable URL was successfully generated and copied',
      });
    } catch (err) {
      openToastAlert({
        alertType: 'error',
        message: 'Something went wrong. Please try to generate shareable URL later',
      });
    }
  }, [saveFilters]);

  return (
    <div className={block()}>
      <div className={element('custom-filters-row')}>
        <FilterButton toggleFilterOpen={onOpenFilterPanel} quantity={totalFilterCount} />
        <FilterPanel
          isOpen={isFilterPanelOpen}
          onClose={onCloseFilterPanel}
          scrollToFilterId={scrollToFilterId}
          onScrollComplete={clearScrollToFilterId}
        />
        <CustomSavedFiltersDropdown variant="toolbar" />
        {isFilterChipShown && (
          <button
            type="button"
            className={element('clear-all-btn')}
            onClick={handleClearAll}
            data-testid="toolbar-clear-all-btn"
          >
            Clear All
          </button>
        )}
        {isDrifted && !isFilterPanelOpen && <ToolbarDriftWarning onClick={onOpenFilterPanel} />}
      </div>
      <div className={element('filter-bar-wrapper')}>
        <Button
          data-testid="shareable-url-button"
          className="shareable-url-button"
          onClick={handleGenerateShareableUrl}
          loading={loading}
        >
          Generate and copy shareable URL
        </Button>
      </div>
    </div>
  );
};

export default genericMemo(TableFilterBar);
